import React, { useRef, useState } from "react";
import { http, authFileUrl, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Camera, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * PhotoUploader — mobile-friendly image picker that uploads directly to our
 * object-storage backed /api/files/upload endpoint.
 *
 * value:     array of relative paths ("/api/files/<id>") or legacy URLs
 * onChange:  (nextArray) => void
 * label:     UI label
 * accent:    "primary" (default) | "secondary"
 * max:       max photos per uploader (default 8)
 */
export default function PhotoUploader({ value = [], onChange, label = "Photos", accent = "primary", max = 8, testId }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  async function pick(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = "";
    if (value.length + files.length > max) {
      toast.error(`Max ${max} photos`);
      return;
    }
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await http.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.url);  // e.g. /api/files/<id>
      }
      onChange([...(value || []), ...uploaded]);
      toast.success(`${uploaded.length} photo${uploaded.length === 1 ? "" : "s"} uploaded`);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  }

  function remove(i) { onChange(value.filter((_, idx) => idx !== i)); }

  const accentClasses = accent === "secondary"
    ? "border-secondary/40 text-secondary hover:bg-secondary/5"
    : "border-primary/40 text-primary hover:bg-primary/5";

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-[11px] text-muted-foreground">{value.length}/{max}</div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {value.map((u, i) => (
          <div key={i} className="relative aspect-square bg-muted rounded overflow-hidden group">
            <img src={authFileUrl(u)} alt="preview" className="w-full h-full object-cover" />
            <button type="button" onClick={() => remove(i)}
              className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-80 hover:opacity-100"
              data-testid={`${testId}-remove-${i}`}>
              <X size={11} />
            </button>
          </div>
        ))}
        {value.length < max && (
          <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
            className={`aspect-square rounded border-2 border-dashed flex flex-col items-center justify-center gap-1 text-xs ${accentClasses} transition disabled:opacity-50`}
            data-testid={`${testId}-add`}>
            {busy ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />}
            <span>{busy ? "Uploading…" : "Add"}</span>
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={pick} />
    </div>
  );
}
